import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Clear } from '@material-ui/icons';
import { debounce } from 'debounce';
import * as styles from './styles/styles.module.scss';
import DropzonePad from '../DropzonePad';
import Modal from '../../shared/Modal';
import ThumbnailEditor from '../ThumbnailEditor';
import TrailFormSuccess from '../../shared/TrailFormSuccess';
import UploadTracker from '../UploadTracker';
import useDropzoneUpload from '../../../hooks/useDropzoneUpload';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import * as formStyles from '../styles/styles.module.scss';

const PhotoUploadModal = ({ exploreFlag, closePhotoModal, onPhotosAdd, url }) => {
  const [thumbnails, toggleThumbnails] = useState([]);
  const [uploading, toggleUploading] = useState(false);
  const [progress, updateProgress] = useState(0);
  const [success, updateSuccess] = useState(false);
  const [scrollPosition, updateUploadScroll] = useState(0);
  const [uploadedPhotos, updateUploadedPhotos] = useState([]);
  const thumbnailsRef = useRef(thumbnails);
  const photoUploadRef = useRef(null);

  const updateScrollPosition = e => {
    updateUploadScroll(e.target.scrollTop);
  };

  const queueCompleteCallback = (hasErrors = false) => {
    if (thumbnailsRef.current.length === 0 || hasErrors) {
      toggleUploading(false);
      return;
    }

    updateProgress(100);
    updateSuccess(true);

    updateUploadedPhotos(photos => {
      if (exploreFlag) {
        const event = new CustomEvent('addPhotos', { detail: photos });
        window.document.dispatchEvent(event);
      } else {
        onPhotosAdd({ detail: photos });
      }

      return [];
    });
  };

  useEffect(() => {
    photoUploadRef.current.addEventListener('scroll', debounce(updateScrollPosition), 200);
  }, []);

  useEffect(() => {
    thumbnailsRef.current = thumbnails;

    if (thumbnails.length !== 0 && thumbnails.length === uploadedPhotos.length) {
      queueCompleteCallback();
    }
  }, [thumbnails, uploadedPhotos]);

  const removedFileCallback = removedFile => {
    const { name } = removedFile;

    toggleThumbnails(prevState => {
      return prevState.filter(({ file }) => {
        return file.name !== name;
      });
    });
  };

  const progressCallback = fileProgress => {
    const progressInt = fileProgress === 100 ? 99 : parseInt(fileProgress.toFixed(0));

    updateProgress(progressInt);
  };

  const thumbnailCallback = (file, dataUrl) => {
    // dont generate a thumbail if the file doesn't pass validation initially
    if (file.status === 'error') {
      return;
    }

    toggleThumbnails(prevState => {
      return [...prevState, { url: dataUrl, caption: '', file, orientation: 0, scale: 1 }];
    });
  };

  const sendingCallback = (file, formData) => {
    const { name } = file;

    const { caption, orientation } = thumbnailsRef.current.find(thumbnail => {
      return thumbnail.file.name === name;
    });

    formData.append('title', caption);
    formData.append('rotation_angle', orientation);
  };

  const successCallback = (file, response) => {
    const { photos } = response;
    updateUploadedPhotos(prevState => [...prevState, { ...photos[0] }]);
  };

  const handleErrorCallback = file => {
    // add error to whatever thumbnail has failed
    const { name } = file;
    Object.assign(file, { hasError: true });

    toggleThumbnails(prevThumbnails => {
      return prevThumbnails.map(thumbnail => {
        if (thumbnail.file.name === name) {
          return { ...thumbnail, file };
        }

        return thumbnail;
      });
    });
  };

  // hooks
  const [dropzone, initDropzone] = useDropzoneUpload({
    url,
    formParam: '#uploadForm',
    paramName: 'image',
    maxFiles: 10,
    maxFilesize: 256,
    acceptedFiles: '.png,.jpg,.jpeg',
    thumbnailCallback,
    progressCallback,
    removedFileCallback,
    successCallback,
    sendingCallback,
    queueCompleteCallback,
    handleErrorCallback
  });

  const cancelUpload = () => {
    dropzone.removeAllFiles(true);
  };

  const deleteFile = file => {
    dropzone.removeFile(file);
  };

  const handleCaptionChange = (e, idx) => {
    const caption = e.currentTarget.value;
    toggleThumbnails(prevThumbnails =>
      prevThumbnails.map((thumb, i) => {
        if (i === idx) {
          return { ...thumb, caption };
        }

        return thumb;
      })
    );
  };

  const configureOrientation = (orientation, amount) => {
    const maxOrientation = 360;
    if (Math.abs(orientation + amount) >= Math.abs(maxOrientation)) {
      return 0;
    }

    return orientation + amount;
  };

  const handleThumbnailRotation = (idx, amount) => {
    toggleThumbnails(prevThumbnails =>
      prevThumbnails.map((thumbnail, index) => {
        if (index === idx) {
          return { ...thumbnail, orientation: configureOrientation(thumbnail.orientation, amount) };
        }

        return thumbnail;
      })
    );
  };

  const closeModal = e => {
    cancelUpload();
    closePhotoModal();
  };

  const openDropzone = () => {
    return dropzone.hiddenFileInput.click();
  };

  const submitPhotos = e => {
    e.preventDefault();

    toggleUploading(true);
    dropzone.processQueue();
  };

  const photoUploadBody = (
    <div className={styles.thumbnailsContainer} ref={photoUploadRef}>
      {thumbnails.length > 0 ? (
        <>
          {thumbnails.map((thumbnail, idx) => {
            if (thumbnail.file.status === 'success') {
              return null;
            }

            return (
              <ThumbnailEditor
                idx={idx}
                {...thumbnail}
                key={`${thumbnail.file.name}`}
                handleCaptionChange={handleCaptionChange}
                handleFileDelete={deleteFile}
                handleThumbnailRotation={handleThumbnailRotation}
                lastThumbnail={idx === thumbnails.length - 1}
                uploading={uploading}
              />
            );
          })}
          <UploadTracker uploading={uploading} progress={progress} openDropzone={openDropzone} />
        </>
      ) : (
        <DropzonePad openDropzone={openDropzone} variant="photos" />
      )}
    </div>
  );

  const uploadButtonText = () => {
    const text = StringConstantsMixin.strings.UPLOAD;
    const toBeUploaded = thumbnails.length - uploadedPhotos.length;
    return toBeUploaded > 0 ? `${text} (${toBeUploaded})` : text;
  };

  return (
    <Modal handleBackgroundClick={closeModal}>
      <div className={formStyles.formUploadContainer}>
        <div className={formStyles.clearContainer}>
          <button type="button" className={formStyles.clearIcon} onClick={closeModal}>
            <Clear />
          </button>
        </div>
        {success ? (
          <TrailFormSuccess variant="photo" />
        ) : (
          <>
            <div className={`${formStyles.trailDataAndClear} ${scrollPosition > 0 ? styles.shadowOnScroll : ''}`}>
              <div className={formStyles.title}>{StringConstantsMixin.strings.CONTRIBUTE.UPLOAD_PHOTOS}</div>
            </div>
            <div id="uploadForm" className={formStyles.form}>
              {photoUploadBody}
              <div className={formStyles.buttonContainer}>
                <button type="button" onClick={submitPhotos} className={formStyles.uploadButton} disabled={thumbnails.length === 0 || uploading}>
                  {uploadButtonText()}
                </button>
                {thumbnails.length !== 0 && (
                  <button className={formStyles.cancelButton} type="button" onClick={cancelUpload}>
                    {StringConstantsMixin.strings.CANCEL}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

PhotoUploadModal.propTypes = {
  url: PropTypes.string,
  exploreFlag: PropTypes.bool,
  closePhotoModal: PropTypes.func.isRequired,
  onPhotosAdd: PropTypes.func
};

PhotoUploadModal.defaultProps = {
  url: '',
  exploreFlag: false,
  onPhotosAdd: () => {}
};

export default PhotoUploadModal;
